import React, { forwardRef, useMemo } from 'react';

import Button from '@/core/components/Button';
import { CityOptions } from '@/core/components/inputs/CityInput/components/CityOptions';
import { DistanceRangeInput } from '@/core/components/inputs/CityInput/components/DistanceRangeInput';
import { InnerLocationInput, InnerLocationInputProps } from '@/core/components/inputs/CityInput/components/InnerLocationInput';
import { LocationDropdown } from '@/core/components/inputs/CityInput/components/LocationDropdown';
import { LocationInputHeader } from '@/core/components/inputs/CityInput/components/LocationInputHeader';
import { LocationInputWrapper } from '@/core/components/inputs/CityInput/components/LocationInputWrapper';
import { LocationSubmitButton } from '@/core/components/inputs/CityInput/components/LocationSubmitButton';
import Separator from '@/core/components/Separator';
import useGeoInput from '@/core/hooks/inputs/useGeoInput';
import { useIsLg } from '@/core/hooks/useMediaQuery';
import { NewInputProps } from '@/core/types/components';
import { LocationForm } from '@/core/types/geo';

export interface LocationInputProps extends NewInputProps<LocationForm | null> {
  isFloating?: boolean;
  locationInputType: 'search' | 'limited' | 'page';
  title?: string;
  validateText: string;
  count?: number;
  loading?: boolean;
  submitIsDisabled?: boolean;
  onClose?: (values?: LocationForm) => void;
}

export const LocationInput = forwardRef<HTMLInputElement, LocationInputProps>(({ locationInputType, ...props }, forwardedRef) => {
  const { search: searchValue, refs, ...rest } = useGeoInput({ ...props, useGeoInputType: locationInputType, forwardedRef });
  const isLg = useIsLg();

  let Component = null;
  switch (locationInputType) {
    case 'search':
      Component = LocationSearchInput;
      break;
    case 'limited':
      Component = LocationLimitedInput;
      break;
    case 'page':
      Component = isLg ? LocationSearchInput : LocationPageMobileInput;
      break;
    default:
      throw Error('"locationInputType" is required');
  }
  return <Component searchValue={searchValue} {...props} {...rest} inputRef={refs.inputRef} divRef={refs.divRef} />;
});

type InnerLocationInput = Omit<LocationInputProps, 'locationInputType'> &
  Pick<InnerLocationInputProps, 'inputRef'> &
  Pick<
    ReturnType<typeof useGeoInput>,
    'isStageAndSelectedEqual' | 'locationValue' | 'isOpen' | 'onOpen' | 'userItem' | 'onSelect' | 'isLoading' | 'isSearching' | 'items' | 'onChangeDistance' | 'onSearch' | 'onClose'
  > & {
    divRef: React.RefObject<HTMLDivElement | null>;
    searchValue: string;
  };

const LocationSearchInput = forwardRef<HTMLInputElement, InnerLocationInput>(
  (
    {
      isOpen,
      locationValue,
      placeholder = '',
      isStageAndSelectedEqual,
      searchValue,
      userItem,
      onSelect,
      isLoading,
      isSearching,
      items,
      inputRef,
      divRef,
      onChangeDistance,
      onSearch,
      title,
      onClose,
      validateText,
      ...props
    },
    _forwardedRef,
  ) => {
    const distance = locationValue?.getLocationFormDistance() ?? 20;
    const itemHasRadius = locationValue?.hasRadius();
    const showDistanceRangeInput = itemHasRadius && !isSearching;
    const { inputValue, inputPlaceholder } = useInputData(
      {
        isOpen,
        locationValue,
        placeholder,
        isStageAndSelectedEqual,
        searchValue,
      },
      true,
    );

    return (
      <LocationInputWrapper divRef={divRef} isOpen={isOpen}>
        <LocationInputHeader isOpen={isOpen} onClose={onClose} placeholder={placeholder} title={title} />
        <InnerLocationInput inputRef={inputRef} inputValue={inputValue} inputPlaceholder={inputPlaceholder} isOpen={isOpen} onSearch={onSearch} {...props} />
        {isOpen ? (
          <LocationDropdown>
            <CityOptions userItem={userItem} onSelect={onSelect(true)} isLoading={isLoading} isSearching={isSearching} items={items} locateMeId="search" />
            {showDistanceRangeInput && (
              <div className="space-y-2 max-lg:hidden">
                <Separator />
                <DistanceRangeInput value={distance} onChange={onChangeDistance} />
              </div>
            )}
            <Button onClick={onClose}>{validateText}</Button>
          </LocationDropdown>
        ) : (
          showDistanceRangeInput && (
            <div className="mt-2 lg:hidden">
              <DistanceRangeInput value={distance} onChange={onChangeDistance} />
            </div>
          )
        )}
      </LocationInputWrapper>
    );
  },
);

const LocationPageMobileInput = forwardRef<HTMLInputElement, InnerLocationInput>(
  (
    {
      isOpen,
      locationValue,
      placeholder = '',
      isStageAndSelectedEqual,
      searchValue,
      userItem,
      onSelect,
      isLoading,
      isSearching,
      items,
      inputRef,
      divRef,
      onChangeDistance,
      onSearch,
      title,
      count,
      loading,
      submitIsDisabled,
      ...props
    },
    _forwardedRef,
  ) => {
    const distance = locationValue?.getLocationFormDistance() ?? 20;
    const itemHasRange = locationValue?.hasRadius();
    const showDistanceRangeInput = itemHasRange && !isSearching;
    const { inputValue, inputPlaceholder } = useInputData(
      {
        isOpen,
        locationValue,
        placeholder,
        isStageAndSelectedEqual,
        searchValue,
      },
      true,
    );

    return (
      <LocationInputWrapper divRef={divRef} isOpen={isOpen}>
        <LocationInputHeader isOpen={isOpen} onClose={props.onClose} placeholder={placeholder} title={title} />
        <InnerLocationInput inputRef={inputRef} inputValue={inputValue} inputPlaceholder={inputPlaceholder} isOpen={isOpen} onSearch={onSearch} {...props} />
        {isOpen && (
          <LocationDropdown>
            <CityOptions userItem={userItem} onSelect={onSelect(false)} isLoading={isLoading} isSearching={isSearching} items={items} locateMeId="search" />
            {showDistanceRangeInput && (
              <div className="space-y-2">
                <Separator />
                <DistanceRangeInput value={distance} onChange={onChangeDistance} />
              </div>
            )}
            <LocationSubmitButton onClick={props.onClose} count={count} isDisabled={submitIsDisabled} loading={loading} />
          </LocationDropdown>
        )}
      </LocationInputWrapper>
    );
  },
);

const LocationLimitedInput = forwardRef<HTMLInputElement, InnerLocationInput>(
  (
    { isOpen, locationValue, placeholder = '', isStageAndSelectedEqual, searchValue, userItem, onSelect, isLoading, isSearching, items, inputRef, divRef, onSearch, title, ...props },
    _forwardedRef,
  ) => {
    const { inputValue, inputPlaceholder } = useInputData(
      {
        isOpen,
        locationValue,
        placeholder,
        isStageAndSelectedEqual,
        searchValue,
      },
      false,
    );

    return (
      <LocationInputWrapper divRef={divRef} isOpen={isOpen}>
        <LocationInputHeader isOpen={isOpen} onClose={props.onClose} placeholder={placeholder} title={title} />
        <InnerLocationInput inputRef={inputRef} inputValue={inputValue} inputPlaceholder={inputPlaceholder} isOpen={isOpen} onSearch={onSearch} {...props} />
        {isOpen && (
          <LocationDropdown>
            <CityOptions userItem={userItem} onSelect={onSelect(true)} isLoading={isLoading} isSearching={isSearching} items={items} locateMeId="limited" />
          </LocationDropdown>
        )}
      </LocationInputWrapper>
    );
  },
);

const useInputData: (
  props: Required<Pick<InnerLocationInput, 'placeholder' | 'locationValue' | 'isOpen' | 'isStageAndSelectedEqual' | 'searchValue'>>,
  hasDistance: boolean,
) => { inputValue: string; inputPlaceholder: string } = ({ placeholder, locationValue, isOpen, isStageAndSelectedEqual, searchValue }, hasDistance) =>
  useMemo(() => {
    if (!locationValue)
      return {
        inputPlaceholder: placeholder,
        inputValue: '',
      };

    const label = locationValue?.getLabel() ?? '';
    const distance = locationValue?.getLocationFormDistance();

    let value = label;
    if (hasDistance && locationValue.hasRadius() && distance !== undefined) {
      value += ` - ${distance}km`;
    }

    if (isOpen)
      return {
        inputValue: value ?? label,
        inputPlaceholder: value,
      };

    return {
      inputValue: value,
      inputPlaceholder: value,
    };
  }, [isOpen, locationValue, placeholder, isStageAndSelectedEqual, searchValue]);
